import axios from 'axios'
import { PublicAxiosInstance } from '@/api/axios'
import { SanitizedHtmlRenderer } from '@/components/html.render'
import Modal from '@/components/modal'
import ConfirmModal from '@/components/modal/confirm-modal'
import { PolicySourceUrlViewer } from '@/components/policy/policy-source-url-viewer'
import SignatureCapture, { type CapturedSignatureResult } from '@/components/signature/signature-capture'
import SignatureOtp from '@/components/signature/signature-otp'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TextArea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FullPageSkeleton } from '@/components/ui/skeleton'
import { signatureFontStack } from '@/constant/signature-fonts'
import { SIGNATURE_DISPLAY_CLASS } from '@/constant/signature-surface'
import { PolicyGenerationMethod, PolicyReviewHistoryStatus, PolicyStatus } from '@/enum/global.enum'
import { useDateFormat } from '@/hooks/use-date-format'
import type { IPolicy } from '@/interface/policy.interface'
import type { ISigningContext } from '@/interface/signature.interface'
import { cn } from '@/lib/utils'
import { formatFileSize, getFileIcon, getFileTypeColor } from '@/utils/file-utils'
import { policyStatusColor, policyStatusLabel } from '@/utils/policy-status-label.utils'
import type { AxiosError } from 'axios'
import { Check, Eye, FileText, Home, MoveRight, User, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer'
import { Navigate, useParams } from 'react-router'
import { toast } from 'sonner'

interface IFile {
  url: string
  name: string
  version: number
  mimeType: string
  size: number
  id: string
  isLatest: boolean
}

interface PolicyData {
  policy: Omit<IPolicy, 'previousReviews' | 'files'> & {
    files: IFile[]
    previousReviews: {
      id: string
      createdAt: Date
      remarks: string
      status: PolicyReviewHistoryStatus
    }[]
    signing?: ISigningContext
  }
  accessInfo: {
    accessedAt: string
    expiresAt: string
  }
}

const PolicyReviewAndApprovalPage = ({ type }: { type: 'review' | 'approval' }) => {
  const { token } = useParams()
  const { formatDate } = useDateFormat()
  const [remarks, setRemarks] = useState('')
  const [loading, setLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [fileLoading, setFileLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [openFileViewer, setOpenFileViewer] = useState(false)
  const [remarksError, setRemarksError] = useState<boolean>(false)
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<IFile | null>(null)
  const [policyData, setPolicyData] = useState<PolicyData | null>(null)
  const [confirmType, setConfirmType] = useState<'reject' | 'approve' | null>(null)
  const [signature, setSignature] = useState<CapturedSignatureResult | null>(null)
  const [otpChallengeId, setOtpChallengeId] = useState<string | null>(null)
  const clearOtpChallenge = useCallback(() => setOtpChallengeId(null), [])

  useEffect(() => {
    const fetchPolicyData = async () => {
      try {
        setLoading(true)

        if (token) {
          // Handle secure token access
          const response = await PublicAxiosInstance.get(`/policy/review-and-approval/${token}`)
          setPolicyData(response.data.data)
        } else {
          setError('No policy identifier provided')
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 410)
          setError('This link has expired. Please contact your organisation admin to request a new link.')
        else if (axios.isAxiosError(err) && err.response?.status === 401)
          setError('This link is invalid. Please request a new link from your administrator.')
        else setError(err instanceof Error ? err.message : 'Failed to load policy')
      } finally {
        setLoading(false)
      }
    }

    fetchPolicyData()
  }, [token])

  // File type detection
  const isImage = selectedFile?.mimeType?.startsWith('image/')
  const isPdf = selectedFile?.mimeType === 'application/pdf'

  // For images and PDFs, we can use direct rendering (no CORS issues)
  // For other files, we'll try DocViewer which may work for some file types
  useEffect(() => {
    if (openFileViewer && selectedFile?.url) {
      // Images and PDFs can be loaded directly without fetch
      if (isImage || isPdf) {
        setFileBlobUrl(selectedFile.url)
        setFileLoading(false)
      } else {
        // For other file types, try to fetch as blob (may fail due to CORS)
        loadFileAsBlob()
      }
    } else {
      // Clean up blob URL when modal closes
      if (fileBlobUrl && !fileBlobUrl.startsWith('http')) {
        URL.revokeObjectURL(fileBlobUrl)
      }
      setFileBlobUrl(null)
      setFileLoading(false)
    }

    return () => {
      // Cleanup on unmount
      if (fileBlobUrl && !fileBlobUrl.startsWith('http')) URL.revokeObjectURL(fileBlobUrl)
    }
  }, [openFileViewer, selectedFile])

  useEffect(() => {
    if (remarks.length < 0) setRemarksError(true)
    else setRemarksError(false)
  }, [remarks])

  const loadFileAsBlob = async () => {
    if (!selectedFile?.url) return

    try {
      setFileLoading(true)
      // For non-image/PDF files, try to fetch as blob
      // This will fail if CORS is not configured, but we'll fallback to direct URL
      try {
        const response = await fetch(selectedFile.url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
        })

        if (!response.ok) throw new Error('Failed to fetch file')

        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        setFileBlobUrl(blobUrl)
      } catch (fetchError) {
        // CORS error - use direct URL and let DocViewer try to handle it
        // Some file types might work with iframe-based rendering
        console.warn('CORS error, using direct URL:', fetchError)
        setFileBlobUrl(selectedFile.url)
      }
    } catch (error) {
      console.error('Error loading file:', error)
      setFileBlobUrl(selectedFile.url)
    } finally {
      setFileLoading(false)
    }
  }

  if (!token) return <Navigate to={'/'} />

  if (loading) return <FullPageSkeleton />

  if (error)
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='text-red-600 text-6xl mb-4'>⚠️</div>
          <h2 className='text-2xl font-bold  mb-2'>Access Error</h2>
          <p className='text-gray-600 mb-4'>{error}</p>
          <Button onClick={() => (window.location.href = '/')}>
            <Home className='h-4 w-4' />
            Go Home
          </Button>
        </div>
      </div>
    )

  if (!policyData) return <Navigate to={'/'} />

  const { policy, accessInfo } = policyData
  const expiresAt = new Date(accessInfo.expiresAt)
  const isExpired = new Date() > expiresAt

  const signing = policy.signing
  // Older links predate the signing metadata; treat a missing block as "not
  // required" so existing in-flight requests keep working.
  const signatureRequired = Boolean(signing?.required && signing?.documentHash)
  const otpRequired = Boolean(signatureRequired && signing?.otpRequired)
  const otpSatisfied = !otpRequired || Boolean(otpChallengeId)
  const canSubmit = (!signatureRequired || Boolean(signature)) && otpSatisfied
  const actionDisabled =
    (type === 'approval' && policy.status !== PolicyStatus.REVIEW) ||
    (type === 'review' && policy.status !== PolicyStatus.REVIEW_REQUESTED)

  if (isExpired)
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='text-red-600 text-6xl mb-4'>⏰</div>
          <h2 className='text-2xl font-bold   mb-2'>Link Expired</h2>
          <p className='text-gray-600 mb-4'>This policy link has expired on {expiresAt.toLocaleDateString()}</p>
          <Button onClick={() => (window.location.href = '/')}>
            <Home className='h-4 w-4' />
            Go Home
          </Button>
        </div>
      </div>
    )

  const handleReviewAndApproval = async (isReject: boolean = false) => {
    if (remarks.length < 0) {
      toast.error(`Please enter your  ${type === 'review' ? 'review' : 'approval'} remark `)
      return
    }

    if (signatureRequired && !signature) {
      toast.error('Please sign and accept the consent statement before submitting')
      return
    }

    try {
      await PublicAxiosInstance.post(`/policy/review-and-approval/${token}`, {
        remarks: remarks,
        isReject: isReject,
        // The document hash is echoed back exactly as it was served, so the
        // server can refuse a signature made against a version the owner has
        // since edited.
        ...(signature && signing?.documentHash
          ? {
              signature: {
                method: signature.method,
                signatureImage: signature.signatureImage,
                typedText: signature.typedText,
                typedFont: signature.typedFont,
                consentAccepted: signature.consentAccepted,
                consentText: signing.consentText,
                documentHash: signing.documentHash,
                saveAsProfile: signature.saveAsProfile,
                useSavedSignature: signature.useSavedSignature,
                ...(otpChallengeId ? { otpChallengeId } : {}),
              },
            }
          : {}),
      })
      toast.success(`Policy ${isReject ? 'rejected' : type === 'review' ? 'reviewed' : 'approved'} successfully`)

      // again fetch the policy data
      if (token) {
        const response = await PublicAxiosInstance.get(`/policy/review-and-approval/${token}`)
        setPolicyData(response.data.data)
      } else {
        setError('No policy identifier provided')
      }
    } catch (err) {
      toast.error(
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
          `Failed to ${type === 'review' ? 'review' : 'approve'} policy`
      )
    }
  }

  // Helper function to get fileType for DocViewer
  const getFileType = (file: IFile | null): string => {
    if (!file) return ''

    // Try to get extension from filename first
    const fileExtension = file.name?.split('.').pop()?.toLowerCase()
    if (fileExtension) return fileExtension

    // Extract extension from MIME type (e.g., "image/png" -> "png")
    if (file.mimeType) {
      const mimeParts = file.mimeType.split('/')
      if (mimeParts.length === 2) {
        const mimeExtension = mimeParts[1].split(';')[0].trim() // Handle cases like "image/png; charset=utf-8"
        // Map common MIME types to extensions
        const mimeToExt: Record<string, string> = {
          jpeg: 'jpg',
          msword: 'doc',
          'vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'vnd.ms-excel': 'xls',
          'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'vnd.ms-powerpoint': 'ppt',
          'vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
        }
        return mimeToExt[mimeExtension] || mimeExtension
      }
    }

    // Fallback to MIME type itself (react-doc-viewer supports MIME types too)
    return file.mimeType
  }

  return (
    <div>
      <div className='flex flex-col'>
        {/* Fixed Header */}
        <div className='flex-shrink-0 p-4 border-b '>
          <div className='max-w-4xl mx-auto flex flex-wrap gap-2 items-center justify-between'>
            <div>
              <h1 className='text-xl font-bold '>{policy.name}</h1>
              <div className='flex items-center gap-2 mt-1'>
                <span className='text-sm text-gray-500'>{policy.policyCategory?.name}</span>
                <Badge className={policyStatusColor(policy.status)}>{policyStatusLabel(policy.status)}</Badge>

                {policy.previousReviews?.[0]?.status ? (
                  <>
                    <MoveRight className='h-4 w-4 mb-[2px]' />
                    <Badge className={policyStatusColor(policy.previousReviews?.[0]?.status)}>
                      {policyStatusLabel(policy.previousReviews?.[0]?.status) === 'Approved'
                        ? type === 'review'
                          ? 'Reviewed'
                          : 'Approved'
                        : 'Rejected'}
                    </Badge>
                  </>
                ) : null}
                {policy.previousReviews?.[0]?.status ? (
                  <span className='text-sm text-gray-500 ml-1'>
                    {formatDate(policy.previousReviews?.[0]?.createdAt)}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className='flex-1 p-4'>
          <div className='mx-auto max-h-[calc(100vh-280px)] max-w-4xl space-y-4 overflow-y-auto'>
            {/* Essential Info - Simplified */}
            <div className=' border rounded-md p-4'>
              <h2 className='text-md font-semibold mb-3 flex items-center gap-1'>
                <User className='h-4 w-4 mb-[2px]' />
                Policy Information
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3 text-sm'>
                <div className='flex justify-between'>
                  <span className='text-gray-500'>Owner:</span>
                  <span>{policy.owner?.name || 'N/A'}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500'>Approver:</span>
                  <span>{policy.approver?.name || 'N/A'}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500'>Next Review:</span>
                  <span>{formatDate(policy.nextReviewDate)}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500'>Link Expires:</span>
                  <span>{formatDate(accessInfo.expiresAt)}</span>
                </div>
              </div>
            </div>
            {/* File Attachments - Simplified */}
            {policy.files && policy.files.length > 0 && (
              <div className='border rounded-md p-4'>
                <h2 className='text-md font-semibold mb-3 flex items-center gap-1'>
                  <FileText className='h-4 w-4 mb-[3px]' />
                  Policy Files ({policy.files.length})
                </h2>
                <div className='space-y-2'>
                  {policy.files.map((file, index) => {
                    const FileIcon = getFileIcon(file.mimeType)
                    return (
                      <div
                        key={index}
                        className='flex items-center justify-between p-2 border rounded hover:bg-muted/20  transition-colors'
                      >
                        <div className='flex items-center gap-3'>
                          <div className='relative'>
                            <FileIcon className={`h-8 w-8 ${getFileTypeColor(file.mimeType)}`} />
                            <div className='absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center'>
                              <span className='text-primary-foreground text-[10px] font-medium'>
                                v{file.version ?? 1}
                              </span>
                            </div>
                          </div>
                          <div>
                            <p className='font-medium text-sm'>{file.name}</p>
                            <div className='flex items-center space-x-1 text-[10px] text-muted-foreground'>
                              <FileText className='h-[10px] w-[10px]' />
                              <span>{formatFileSize(file.size)}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          size='sm'
                          variant='outline'
                          onClick={() => {
                            setSelectedFile(file)
                            setOpenFileViewer(true)
                          }}
                          className='flex items-center gap-1 text-xs'
                        >
                          <Eye className='h-3 w-3' />
                          View
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {/* Policy Description - Simplified */}
            {policy?.description &&
              (policy?.generationMethod === PolicyGenerationMethod.MANUAL ||
                policy?.generationMethod === PolicyGenerationMethod.AI) && (
                <div className='border rounded-md p-4'>
                  <SanitizedHtmlRenderer htmlContent={policy?.description ?? ''} />
                </div>
              )}

            {policy?.sourceUrl && policy?.generationMethod === PolicyGenerationMethod.URL ? (
              <PolicySourceUrlViewer url={policy.sourceUrl} />
            ) : null}
          </div>
        </div>

        {/* Fixed Checkbox at Bottom */}
        <div className='flex-shrink-0 p-4 '>
          {policy?.previousReviews && policy?.previousReviews?.length > 0 ? (
            <div className='max-w-4xl mx-auto flex flex-col gap-2 border-t border-border  pt-4'>
              <div className='space-y-5'>
                <div>
                  <Label className='text-sm text-gray-500'>Remarks</Label>
                  <Label>{policy?.previousReviews?.[0]?.remarks ?? ''}</Label>
                </div>
                <div className='flex gap-6'>
                  <div>
                    <Label className='text-sm text-gray-500'>Status</Label>
                    <Badge className={policyStatusColor(policy.previousReviews[0].status)}>
                      {policyStatusLabel(policy.previousReviews[0].status) === 'Approved'
                        ? type === 'review'
                          ? 'Reviewed'
                          : 'Approved'
                        : 'Rejected'}
                    </Badge>
                  </div>
                  <div>
                    <Label className='text-sm text-gray-500'>Date</Label>
                    <Label className='pt-2'>{formatDate(policy.previousReviews?.[0]?.createdAt)}</Label>
                  </div>
                </div>

                {/* Show the signature that was actually applied, so the signer
                    can see the record their decision produced. */}
                {signing?.signatures?.length ? (
                  <div className='space-y-2'>
                    <Label className='text-sm text-gray-500'>Signature</Label>
                    {signing.signatures.map((applied) => (
                      <div key={applied.id} className='rounded-md border p-3'>
                        <p className='text-sm font-medium'>{applied.signerName}</p>
                        {applied.typedText ? (
                          <p
                            className={cn('text-2xl leading-tight', SIGNATURE_DISPLAY_CLASS)}
                            style={{ fontFamily: signatureFontStack(null) }}
                          >
                            {applied.typedText}
                          </p>
                        ) : applied?.signatureImage ? (
                          <img
                            src={applied.signatureImage}
                            alt={`Signature of ${applied.signerName}`}
                            className={cn('max-h-16 object-contain', SIGNATURE_DISPLAY_CLASS)}
                          />
                        ) : null}
                        <p className='mt-1 text-xs text-muted-foreground'>
                          {applied.intent === 'REJECTED'
                            ? 'Rejected'
                            : applied.intent === 'APPROVED'
                              ? 'Approved'
                              : 'Reviewed'}
                          {' · '}
                          {formatDate(applied.signedAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className='max-w-4xl mx-auto flex flex-col gap-3 border-t border-border pt-4'>
              <TextArea
                id='remarks'
                value={remarks}
                onChange={(e) => {
                  setRemarks(e.target.value)
                }}
                placeholder={
                  type === 'review' ? 'Enter your review remark (required)' : 'Enter your approval remark (required)'
                }
                className={cn('h-[100px]')}
                disabled={actionDisabled}
                error={remarksError ? 'Please enter your remark' : ''}
              />

              {otpRequired && token ? (
                <SignatureOtp
                  token={token}
                  onVerified={setOtpChallengeId}
                  onExpired={clearOtpChallenge}
                  disabled={actionDisabled}
                />
              ) : null}

              {signatureRequired && signing ? (
                <SignatureCapture
                  signing={signing}
                  onChange={setSignature}
                  disabled={actionDisabled || !otpSatisfied}
                />
              ) : null}

              <div className='flex gap-2 justify-end'>
                <Button
                  onClick={() => {
                    if (remarks.length < 1) {
                      setRemarksError(true)
                      return
                    }
                    setConfirmType('reject')
                    setOpenModal(true)
                  }}
                  disabled={actionDisabled || !canSubmit}
                  variant={'destructive'}
                >
                  <X className='h-4 w-4' />
                  Reject
                </Button>
                <Button
                  onClick={() => {
                    if (remarks.length < 1) {
                      setRemarksError(true)
                      return
                    }
                    setConfirmType('approve')
                    setOpenModal(true)
                  }}
                  disabled={actionDisabled || !canSubmit}
                >
                  <Check className='h-4 w-4' />
                  {type === 'review' ? 'Submit Review' : 'Submit Approval'}
                </Button>
              </div>

              {signatureRequired && !signature ? (
                <p className='text-right text-xs text-muted-foreground'>
                  Sign and accept the consent statement to continue.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <Modal size='xl' open={openFileViewer} setOpen={setOpenFileViewer} title={selectedFile?.name ?? ''}>
          <div className='h-[calc(80vh-100px)] overflow-hidden'>
            {fileLoading ? (
              <div className='flex items-center justify-center h-full'>
                <div className='text-center'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
                  <p className='text-muted-foreground'>Loading file...</p>
                </div>
              </div>
            ) : selectedFile?.url ? (
              <>
                {/* Render images directly - no CORS issues */}
                {isImage && (
                  <div className='w-full h-full flex items-center justify-center bg-black/5 dark:bg-white/5 rounded-lg overflow-auto'>
                    <img
                      loading='lazy'
                      decoding='async'
                      src={selectedFile.url}
                      alt={selectedFile.name || 'Image'}
                      className='max-w-full max-h-full object-contain bg-white'
                    />
                  </div>
                )}

                {/* Render PDFs directly using iframe - no CORS issues */}
                {isPdf && (
                  <div className='w-full h-full'>
                    <iframe
                      src={`${selectedFile.url}#toolbar=1&navpanes=1&scrollbar=1`}
                      className='w-full h-full border rounded-lg'
                      title={selectedFile.name || 'PDF Document'}
                      style={{ minHeight: '600px' }}
                    />
                  </div>
                )}

                {/* Use DocViewer for other file types */}
                {!isImage && !isPdf && fileBlobUrl && (
                  <DocViewer
                    documents={[
                      {
                        uri: fileBlobUrl,
                        fileType: getFileType(selectedFile),
                      },
                    ]}
                    pluginRenderers={DocViewerRenderers}
                    style={{ height: '100%' }}
                    config={{
                      header: {
                        disableHeader: true,
                        disableFileName: true,
                      },
                    }}
                  />
                )}
              </>
            ) : (
              <div className='flex items-center justify-center h-full'>
                <div className='text-center'>
                  <p className='text-muted-foreground mb-4'>Failed to load file</p>
                  {selectedFile?.url && (
                    <Button variant='outline' onClick={() => window.open(selectedFile.url, '_blank')}>
                      Open in New Tab
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>

      <ConfirmModal
        open={openModal}
        setOpen={setOpenModal}
        title={confirmType === 'reject' ? 'Reject Policy' : type === 'review' ? 'Review Policy' : 'Approve Policy'}
        description={
          confirmType === 'reject'
            ? 'Are you sure you want to reject this policy?'
            : type === 'review'
              ? 'Are you sure you want to review this policy?'
              : 'Are you sure you want to approve this policy?'
        }
        buttonLabel={
          confirmType === 'reject' ? 'Confirm Reject' : type === 'review' ? 'Confirm Review' : 'Confirm Approve'
        }
        loadingButtonLabel={
          confirmType === 'reject' ? 'Rejecting...' : type === 'review' ? 'Reviewing...' : 'Approving...'
        }
        isNested={true}
        submitButtonVariant={confirmType === 'reject' ? 'destructive' : 'default'}
        cancelButtonVariant='outline'
        hideToast
        onSubmit={async () => {
          setConfirmLoading(true)
          if (confirmType === 'reject') await handleReviewAndApproval(true)
          else if (confirmType === 'approve') await handleReviewAndApproval(false)
          setConfirmLoading(false)
        }}
        loading={confirmLoading}
      />
    </div>
  )
}

export default PolicyReviewAndApprovalPage
